import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import api from '../services/api';

const Blog = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(9);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [favorites, setFavorites] = useState({});

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setIsLoading(true);
        // Request all posts (set a large limit to get all published posts)
        const response = await api.get('/blog?limit=100');
        setPosts(response.data.posts);
        setError(null);

        // Extract favorite status from posts (now included in API response)
        if (response.data.posts) {
          const favoriteStatus = {};
          response.data.posts.forEach(post => {
            favoriteStatus[post.id] = post.is_favorited || false;
          });
          setFavorites(favoriteStatus);
        }
      } catch (err) {
        console.error('Error fetching blog posts:', err);
        setError('Failed to fetch blog posts. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPosts();
  }, []);

  // Extract first image from markdown content
  const extractFirstImage = (content) => {
    if (!content) return null;
    const imageRegex = /!\[.*?\]\((.*?)\)/;
    const match = content.match(imageRegex);
    return match ? match[1] : null;
  };

  // Get cover image (from cover_image_url or first image in content)
  const getCoverImage = (post) => {
    return post.cover_image_url || extractFirstImage(post.content);
  };

  // Fixed predefined categories (matching admin category options)
  const predefinedCategories = [
    'Technology',
    'AI & Machine Learning',
    'Marketing',
    'Business',
    'Design',
    'Development',
    'Data Science',
    'Product',
    'Industry News',
    'Tutorials',
    'Case Studies',
    'Best Practices'
  ];

  // Get categories that have posts
  const categoriesWithPosts = predefinedCategories.filter(category =>
    posts.some(post => post.category === category)
  );

  // Add 'all' at the beginning and 'favorites' at the end
  const categories = ['all', ...categoriesWithPosts, 'favorites'];

  // Filter posts by category or favorites
  const filteredPosts = selectedCategory === 'all'
    ? posts
    : selectedCategory === 'favorites'
    ? posts.filter(post => favorites[post.id])
    : posts.filter(post => post.category === selectedCategory);

  // Pagination
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

  // Format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  // Format number with K suffix
  const formatNumber = (num) => {
    if (!num || num === 0) return '0';
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  // Toggle favorite
  const handleToggleFavorite = async (e, postId) => {
    e.stopPropagation();

    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please login to favorite posts');
      navigate('/login');
      return;
    }

    try {
      const response = await api.post(`/blog/${postId}/favorite`);
      setFavorites(prev => ({
        ...prev,
        [postId]: response.data.isFavorited
      }));

      // Update the post's favorites count in the list
      setPosts(prevPosts =>
        prevPosts.map(post =>
          post.id === postId
            ? { ...post, favorites_count: response.data.favoritesCount }
            : post
        )
      );
    } catch (err) {
      console.error('Error toggling favorite:', err);
      console.error('Error details:', err.response?.data);
      // If not authenticated, show a login prompt
      if (err.response?.status === 401) {
        alert('Please login to favorite posts');
        navigate('/login');
      } else {
        alert('Failed to update favorite. Please try again.');
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="relative w-16 h-16 mx-auto mb-6">
                <div className="absolute top-0 left-0 w-full h-full border-4 border-primary-200 rounded-full animate-ping"></div>
                <div className="absolute top-0 left-0 w-full h-full border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
              <p className="text-gray-600 font-medium">Loading articles...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <p className="text-gray-600 mb-6 font-medium">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:shadow-lg transform hover:scale-105 transition-all font-medium"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-white">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
            {/* Modern Header Section */}
            <div className="mb-6">
              <div className="flex items-baseline gap-4 flex-wrap">
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                  Latest Articles
                </h1>
                <p className="text-base text-gray-500">
                  Explore insights on AI, marketing, and technology
                </p>
              </div>
            </div>

            {/* Horizontal Category Filter */}
            <div className="mb-6">
              <div className="border-b border-gray-200">
                <div className="overflow-x-auto scrollbar-hide">
                  <div className="flex gap-1 min-w-max pb-px">
                    {categories.map((category) => {
                      const count = category === 'all'
                        ? posts.length
                        : category === 'favorites'
                        ? Object.values(favorites).filter(isFav => isFav).length
                        : posts.filter(p => p.category === category).length;

                      return (
                        <button
                          key={category}
                          onClick={() => {
                            setSelectedCategory(category);
                            setCurrentPage(1);
                          }}
                          className={`px-6 py-3 font-medium whitespace-nowrap transition-all relative ${
                            selectedCategory === category
                              ? 'text-primary-600'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            <span className="flex items-center gap-1.5">
                              {category === 'favorites' && (
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                </svg>
                              )}
                              {category === 'all' ? 'All Articles' : category === 'favorites' ? 'Favorites' : category}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              selectedCategory === category
                                ? 'bg-primary-100 text-primary-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {count}
                            </span>
                          </span>
                          {selectedCategory === category && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600"></div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 min-w-0">

                {/* Blog Posts Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {currentPosts.map((post) => {
                    const coverImage = getCoverImage(post);
                    const isFavorited = favorites[post.id] || false;

                    return (
                      <article
                        key={post.id}
                        onClick={() => navigate(`/blog/${post.slug}`)}
                        className="group bg-white rounded-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-200 hover:border-primary-200 flex flex-col"
                      >
                        {/* Cover Image */}
                        <div className="relative h-48 overflow-hidden bg-gray-100">
                          {coverImage ? (
                            <>
                              <img
                                src={coverImage}
                                alt={post.title}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                              <svg className="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                              </svg>
                            </div>
                          )}

                          {/* Category Badge */}
                          {post.category && (
                            <div className="absolute top-3 left-3">
                              <span className="px-3 py-1 bg-white/95 backdrop-blur-sm text-primary-700 rounded-md text-xs font-semibold shadow-sm">
                                {post.category}
                              </span>
                            </div>
                          )}

                          {/* Favorite Button */}
                          <button
                            onClick={(e) => handleToggleFavorite(e, post.id)}
                            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-all shadow-sm hover:scale-110"
                          >
                            <svg
                              className={`w-5 h-5 transition-colors ${
                                isFavorited ? 'text-red-500 fill-current' : 'text-gray-600'
                              }`}
                              fill={isFavorited ? "currentColor" : "none"}
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                          </button>
                        </div>

                        {/* Content */}
                        <div className="p-5 flex flex-col flex-1">
                          {/* Title */}
                          <h2 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors leading-tight">
                            {post.title}
                          </h2>

                          {/* Excerpt */}
                          <p className="text-gray-600 mb-4 line-clamp-3 text-sm leading-relaxed flex-1">
                            {post.excerpt || 'Click to read more...'}
                          </p>

                          {/* Stats and Meta */}
                          <div className="space-y-3 mt-auto">
                            {/* Stats Row */}
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              <div className="flex items-center gap-1.5">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                <span>{formatNumber(post.views || 0)} views</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                </svg>
                                <span>{formatNumber(post.favorites_count || 0)} favorites</span>
                              </div>
                            </div>

                            {/* Author and Date */}
                            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
                                  <span className="text-white text-xs font-semibold">
                                    {post.author_name?.charAt(0) || 'A'}
                                  </span>
                                </div>
                                <span className="text-sm font-medium text-gray-700">{post.author_name}</span>
                              </div>
                              <span className="text-xs text-gray-500">
                                {formatDate(post.published_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-12 flex justify-center">
                    <nav className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                        disabled={currentPage === 1}
                        className="p-2 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>

                      {Array.from({ length: totalPages }, (_, index) => (
                        <button
                          key={index + 1}
                          onClick={() => setCurrentPage(index + 1)}
                          className={`min-w-[40px] h-10 rounded-lg font-semibold transition-all ${
                            currentPage === index + 1
                              ? 'bg-primary-600 text-white shadow-md'
                              : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                          }`}
                        >
                          {index + 1}
                        </button>
                      ))}

                      <button
                        onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="p-2 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </nav>
                  </div>
                )}

                {/* Empty State */}
                {filteredPosts.length === 0 && (
                  <div className="text-center py-24">
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
                      {selectedCategory === 'favorites' ? (
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                      ) : (
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                        </svg>
                      )}
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {selectedCategory === 'favorites' ? '没有收藏的文章' : 'No Articles Found'}
                    </h3>
                    <p className="text-gray-600 mb-6">
                      {selectedCategory === 'all'
                        ? 'Check back later for new content'
                        : selectedCategory === 'favorites'
                        ? '浏览文章并点击爱心图标来收藏您喜欢的内容'
                        : `No articles in "${selectedCategory}" category yet`}
                    </p>
                    {selectedCategory !== 'all' && (
                      <button
                        onClick={() => setSelectedCategory('all')}
                        className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-all font-medium"
                      >
                        View All Articles
                      </button>
                    )}
                  </div>
                )}
              </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blog;
